import { generateColorCssVariable, type RgbColorHex, SharedUiColorName } from '@/shared/ui/theming';
import type { TooltipFormatterContext } from '@/ui/chart/builder/parts/tooltip.utils';
import { type IconName, UiIconComponent } from '@/ui/icon/ui-icon';
import { I18nService } from '@/utils/i18n/i18n.service';
import { combineLatestDeepObject } from '@/utils/rxjs/generators/combine-latest-deep-object.generator';
import { extractValue } from '@/utils/rxjs/helpers/rxjs.helpers';
import { concatReducer } from '@/utils/toolkit/array/concat.utils';
import type { primitive } from '@/utils/typing/primitive.type';
import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { type BehaviorSubject, combineLatest, type Observable, zip } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { HighchartsChartComponent } from '../../../../components/highcharts-chart/highcharts-chart';
import { UnitCode } from '../../../../interfaces/Unit.interface';
import type { RegularObject } from '../../../../interfaces/utils.interface';
import { SharedUiColoredTextColorDirective } from '../../../../libs/shared/ui/theming/lib/colors/shared-ui-color.directives';
import { CustomTranslatePipe } from '../../../../pipes/custom-translate.pipe';
import { FormatterProvider } from '../../../../providers/formatter/formatter.provider';
import { HighchartsStaticProvider } from '../../../../providers/highcharts/highcharts.static.provider';
import { UnitProvider } from '../../../../providers/unit/unit.provider';
import type { DataSource } from '../../../data-source.class';
import {
    AggregationFormatting,
    type AggregationMethod,
    type CustomTranslation,
    type DataSourceMetaData,
    type DataSourceStatus,
    type GroupedData,
    type ValueFormula
} from '../../../data-source.interface';
import { DataSourceProvider } from '../../../data-source.provider';
import { getTransformFunctionFromFormula } from '../../../data-source.utils';
import { DataSourcedWidgetProvider } from '../../../data-sourced-widget.provider';
import type { AggregatedData, AggregationDescription } from '../../data-viz-widget.interface';
import { WidgetGhost } from '../widget-ghost.class';
import type { WidgetGhostConfig } from '../widget-ghost.interface';

interface PreComputedData {
    groupedDataSet: GroupedData<any>[];
    aggregatedData: AggregatedData<any>[];
    formatterFunctions: ((valueToFormat: number) => string | Observable<string>)[];
    columnConfigs: ComparisonColumn[];
}

interface DifferencialDisplay {
    conditionFormula: ValueFormula;
    color: RgbColorHex;
    icon: IconName;
    text: CustomTranslation;
}

interface DifferencialData {
    absoluteGap: string;
    percentGap: string;
    reversedPercentGap: string;
    differencialSettings: DifferencialDisplay;
}

interface ComparisonProcessedData {
    chartConfig: Highcharts.Options;
    differencialData: DifferencialData;
}

interface ComparisonColumn {
    columnTitle: CustomTranslation;
    columnAggregation: string;
    aggregationMethod: AggregationMethod;
    splitSeriesOn?: AggregationDescription;
}

export interface ComparisonGhostConfig extends WidgetGhostConfig {
    dataSource: string;
    differencialDisplay: DifferencialDisplay[];
    columns: {
        firstColumn: ComparisonColumn;
        secondColumn: ComparisonColumn;
    };
}

@Component({
    styleUrls: ['./comparison-ghost.scss'],
    selector: 'comparison-ghost',
    templateUrl: 'comparison-ghost.html',
    imports: [HighchartsChartComponent, UiIconComponent, SharedUiColoredTextColorDirective, AsyncPipe, CustomTranslatePipe]
})
export class ComparisonGhostComponent extends WidgetGhost<ComparisonGhostConfig, ComparisonProcessedData> {
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);
    private _dataSourcedWidgetProvider = inject(DataSourcedWidgetProvider);
    private _dataSourceProvider = inject(DataSourceProvider);
    private _formatterProvider = inject(FormatterProvider);
    private _i18n = inject(I18nService);
    private _unitProvider = inject(UnitProvider);

    dataSources$!: BehaviorSubject<RegularObject<DataSource<any>>>;
    config$!: BehaviorSubject<ComparisonGhostConfig>;

    processedData$!: Observable<ComparisonProcessedData>;

    processStatus$!: BehaviorSubject<DataSourceStatus>;
    status$!: Observable<DataSourceStatus>;

    // Widget Ghost Implementation

    process$(dataSources: RegularObject<DataSource<any>>, config: ComparisonGhostConfig): Observable<ComparisonProcessedData> {
        const dataSource = dataSources[config.dataSource];
        const metadata = dataSource.getMetaData();
        return this.groupData$(dataSource, config).pipe(
            map(groupedData => this.preComputeGroupedData(groupedData, config, metadata)),
            switchMap(preComputedGroupedData => this.buildProcessedDataFromPreComputedData$(preComputedGroupedData, metadata))
        );
    }

    hasData(comparisonProcessedData: ComparisonProcessedData): boolean {
        return (
            !!comparisonProcessedData.chartConfig.series &&
            comparisonProcessedData.chartConfig.series.some((series: any) => series.data && series.data.length)
        );
    }

    exportAsCsv$<ExportedData>(
        _dataSources: RegularObject<DataSource<any>>,
        _config: ComparisonGhostConfig
    ): Observable<{ data: ExportedData[]; columns: { header: string; value: (line: ExportedData) => string | number | boolean }[] }> {
        throw new Error('Method not implemented.');
    }

    getDataSourcesWithMeaningfulStatus(dataSources: RegularObject<DataSource<any>>, config: ComparisonGhostConfig): DataSource<any>[] {
        return dataSources[config.dataSource] ? [dataSources[config.dataSource]] : [];
    }

    // Process methods

    private applyFormulaAndRetrieveAssociatedSetting(value: any, settings: DifferencialDisplay[]): DifferencialDisplay | null | undefined {
        if (!settings) return null;
        return settings.find(setting => getTransformFunctionFromFormula(setting.conditionFormula)({ value }));
    }

    // Returns two sets of GroupedData, for the first and second columns
    private groupData$(dataSource: DataSource<any>, config: ComparisonGhostConfig): Observable<GroupedData<any>[]> {
        const columns = Object.values(config.columns);
        const groupedDataByColumn$ = columns.map(column =>
            column.splitSeriesOn ? dataSource.group([column.splitSeriesOn.column]) : dataSource.group([column.columnAggregation])
        );
        return zip(...groupedDataByColumn$);
    }

    private aggregateData(groupedData: GroupedData<any>, columnConfig: ComparisonColumn): AggregatedData<any> {
        return this._dataSourcedWidgetProvider.aggregateGroupedData(
            groupedData,
            columnConfig.aggregationMethod,
            columnConfig.columnAggregation
        );
    }

    private formatColumnsAggregatedDataToSeriesData(metadata: DataSourceMetaData<any>, precomputedData: PreComputedData) {
        const format = (formatting: AggregationFormatting, value: primitive, columnIndex: number) => {
            const columnConfig = precomputedData.columnConfigs[columnIndex];
            if (columnConfig.splitSeriesOn) {
                return this._dataSourcedWidgetProvider.formatAggregation(
                    formatting,
                    value,
                    columnConfig.splitSeriesOn,
                    precomputedData.groupedDataSet[columnIndex].values[columnConfig.splitSeriesOn.column],
                    metadata
                );
            } else {
                if (formatting === AggregationFormatting.NAME) {
                    return value;
                } else if (formatting === AggregationFormatting.COLOR) {
                    return `var(${generateColorCssVariable(SharedUiColorName.SECONDARY)})`;
                } else return;
            }
        };

        const aggregatedMapEntries = precomputedData.aggregatedData.map(aggregatedDataPoint =>
            Object.entries(aggregatedDataPoint.aggregatedMap)
        );
        return aggregatedMapEntries.map((mapEntries, columnIndex) =>
            mapEntries
                .map(
                    ([columnName, columnValue]) =>
                        ({
                            y: columnValue,
                            name: format(AggregationFormatting.NAME, columnName, columnIndex),
                            color: this._dataSourcedWidgetProvider.adaptColorForHighcharts(
                                format(AggregationFormatting.COLOR, columnName, columnIndex) as string | Observable<string>
                            )
                        }) as Highcharts.PointOptionsObject
                )
                .filter(point => !!point.y)
        );
    }

    private generateSeries(
        highchartsFormattedColumnSeries: Highcharts.PointOptionsObject[][]
    ): Highcharts.SeriesOptionsRegistry['SeriesColumnOptions'][] {
        return highchartsFormattedColumnSeries
            .map((columnSeries, columnIndex) =>
                columnSeries.map(
                    point =>
                        ({
                            type: 'column',
                            name: point.name,
                            data: columnIndex === 0 ? [{ y: point.y, color: point.color }] : [null, { y: point.y, color: point.color }]
                        }) as Highcharts.SeriesOptionsRegistry['SeriesColumnOptions']
                )
            )
            .reduce(concatReducer, []);
    }

    private preComputeGroupedData(
        groupedDataSet: GroupedData<any>[],
        config: ComparisonGhostConfig,
        metadata: DataSourceMetaData<any>
    ): PreComputedData {
        const columnConfigs = Object.values(config.columns);

        const aggregatedData = groupedDataSet.map((groupedData, columnIndex) => {
            return this.aggregateData(groupedData, columnConfigs[columnIndex]);
        });

        const formatterFunctions = columnConfigs.map((columnConfig, columnIndex) =>
            this._formatterProvider.getTypeTextFormatter(
                this._dataSourceProvider.adaptColumnMetaDataForAggregationMethod(
                    metadata.columns[columnConfig.columnAggregation],
                    columnConfig.aggregationMethod
                ).typeConfig,
                aggregatedData[columnIndex].allAggregatedLines,
                aggregatedData[columnIndex].dataRegularities,
                aggregatedData[columnIndex].allValues
            )
        );

        return {
            groupedDataSet,
            aggregatedData,
            formatterFunctions,
            columnConfigs
        };
    }

    private computeAllDifferencialValues$(preComputedGroupedData: PreComputedData): Observable<DifferencialData> {
        const firstColumnTotal: number = (preComputedGroupedData.aggregatedData[0].allValues as number[]).reduce(
            (acc: number, curr: number) => acc + curr,
            0
        ) as number;
        const secondColumnTotal: number = (preComputedGroupedData.aggregatedData[1].allValues as number[]).reduce(
            (acc: number, curr: number) => acc + curr,
            0
        ) as number;
        const differencial: number = secondColumnTotal - firstColumnTotal;

        return combineLatestDeepObject({
            differencialSettings: this.applyFormulaAndRetrieveAssociatedSetting(differencial, this.config.differencialDisplay),
            absoluteGap: preComputedGroupedData.formatterFunctions[0](Math.abs(differencial)),
            percentGap: this._unitProvider.formatterFactory(
                UnitCode.PERCENT,
                UnitCode.PERCENT
            )(Math.abs(differencial / secondColumnTotal) * 100),
            reversedPercentGap: this._unitProvider.formatterFactory(
                UnitCode.PERCENT,
                UnitCode.PERCENT
            )(Math.abs(differencial / firstColumnTotal) * 100)
        });
    }

    private buildChart$(metadata: DataSourceMetaData<any>, precomputedData: PreComputedData): Observable<Highcharts.Options> {
        const formattedData = this.formatColumnsAggregatedDataToSeriesData(metadata, precomputedData);
        const series = this.generateSeries(formattedData);

        return combineLatestDeepObject(
            this._highchartsStaticProvider.buildConfig(
                this._highchartsStaticProvider.REFLOW_ON_CONTAINER_RESIZE,
                this._highchartsStaticProvider.DISABLE_ANIMATIONS,
                this._highchartsStaticProvider.COLUMN_MIN_SIZE_5,
                {
                    chart: {
                        type: 'column'
                    },
                    title: {
                        text: null
                    },
                    xAxis: {
                        categories: precomputedData.columnConfigs.map(columnConfig =>
                            this._i18n.translateCustomTranslation$(columnConfig.columnTitle)
                        ),
                        visible: true,
                        lineColor: 'transparent',
                        labels: {
                            style: {
                                fontWeight: 'bold',
                                fontSize: 12
                            }
                        }
                    },
                    yAxis: {
                        endOnTick: false,
                        stackLabels: {
                            enabled: true,
                            style: {
                                fontWeight: 'bold',
                                fontSize: 12
                            },
                            overflow: 'allow',
                            crop: false,
                            formatter(this: any) {
                                return extractValue(precomputedData.formatterFunctions[this.x](this.total));
                            }
                        },
                        gridLineWidth: 0,
                        labels: {
                            enabled: false
                        },
                        plotLines: [
                            {
                                color: 'lightgrey',
                                width: 1,
                                value: 0
                            }
                        ]
                    },
                    plotOptions: {
                        column: {
                            pointPadding: 0.2,
                            borderWidth: 0,
                            stacking: 'normal'
                        }
                    },
                    tooltip: {
                        outside: true,
                        hideDelay: 100,
                        formatter(this: TooltipFormatterContext) {
                            return `${this.key}<br />
                            ${series ? `${this.series.name}: ` : ''}<b>${extractValue(
                                precomputedData.formatterFunctions[this.point.x](this.point.y!)
                            )}</b>
                        `;
                        }
                    },
                    series
                }
            )
        );
    }

    private buildProcessedDataFromPreComputedData$(
        preComputedGroupedData: PreComputedData,
        metadata: DataSourceMetaData<any>
    ): Observable<ComparisonProcessedData> {
        return combineLatest([
            this.buildChart$(metadata, preComputedGroupedData),
            this.computeAllDifferencialValues$(preComputedGroupedData)
        ]).pipe(
            map(([chartConfig, differencialData]) => ({
                chartConfig,
                differencialData
            }))
        );
    }
}
